import { LuneTheme } from '@lune-fe/lune-ui-lib'
import Box from '@mui/material/Box'
import Stack from '@mui/material/Stack'
import Text from '@mui/material/Typography'
import { ReactNode, useMemo } from 'react'

import { ChipType, EmissionFactorChip } from '@/components/MovingChipAnimation/EmissionFactorChip'

const ANIMATION_WIDTH = '2120px'

export const BigText = ({ text }: { text: string }) => {
    const { palette } = LuneTheme
    return (
        <Text
            sx={{
                fontSize: '160px',
                fontWeight: '300',
                lineHeight: '192px',
                color: palette.Grey200,
            }}
        >
            {text}
        </Text>
    )
}

const CarouselShadow = ({ rotate }: { rotate?: boolean }) => {
    return useMemo(() => {
        return (
            <Box
                sx={{
                    position: 'absolute',
                    bottom: '0',
                    top: '0',
                    left: rotate ? 'auto' : '0',
                    right: rotate ? '0' : 'auto',
                    width: '100px',
                    transform: rotate ? 'rotate(180deg)' : 'none',
                    zIndex: 99,
                    background: 'linear-gradient(90deg, #FFFFFF 0%, rgba(255, 255, 255, 0) 100%)',
                }}
            />
        )
    }, [rotate])
}

export const sharedRowStyle = {
    willChange: 'transform',
    backfaceVisibility: 'hidden',
    perspective: 1000,
    display: 'flex',
    width: 'max-content',
    justifyContent: 'flex-start',
    '.animated-item': {
        overflow: 'hidden',
        display: 'flex',
        justifyContent: 'flex-start',
        gap: 5,
    },
}

export const Row = ({ children }: { children: React.ReactNode }) => {
    return (
        <Box
            sx={{
                position: 'relative',
                height: '220px',
                width: '100%',
                overflow: 'hidden',
                margin: 'auto',
            }}
        >
            {children}
        </Box>
    )
}

export const DesktopAnimationWrapper = ({ children }: { children: ReactNode }) => {
    return (
        <Box sx={{ display: 'flex', justifyContent: 'center' }}>
            <Box sx={{ position: 'relative', width: ANIMATION_WIDTH }}>
                <CarouselShadow />
                <Box
                    sx={{
                        backgroundImage: `url(${'images/moving-chip-animation/grid-desktop.png'})`,
                        backgroundSize: 'cover',
                        height: '656px',
                        position: 'absolute',
                        marginTop: '64px',
                        width: ' 100%',
                    }}
                />
                <Stack spacing={8}>{children}</Stack>
                <CarouselShadow rotate />
            </Box>
        </Box>
    )
}

export const SmallScreenDisplayWrapper = ({ children }: { children: ReactNode }) => {
    return (
        <Box sx={{ position: 'relative' }}>
            <Box
                sx={{
                    backgroundImage: `url(${'images/moving-chip-animation/grid-mobile.png'})`,
                    backgroundSize: 'cover',
                    width: '150%',
                    height: '100%',
                    left: '-25%',
                    right: '-25%',
                    position: 'absolute',
                    zIndex: -1,
                }}
            />
            <Box sx={{ position: 'absolute', left: -70, top: 80 }}>
                <EmissionFactorChip type={ChipType.Truck} />
            </Box>
            <Box sx={{ position: 'absolute', right: 60, top: 500 }}>
                <EmissionFactorChip type={ChipType.Wheat} />
            </Box>
            <Box sx={{ position: 'absolute', left: 0, top: 925 }}>
                <EmissionFactorChip type={ChipType.Bottle} />
            </Box>
            <Stack spacing={1} sx={{ width: '100%' }}>
                {children}
            </Stack>
        </Box>
    )
}

export const CustomerSmallScreenDisplayWrapper = ({ children }: { children: ReactNode }) => {
    return (
        <Box sx={{ position: 'relative' }}>
            <Box
                sx={{
                    backgroundImage: `url(${'images/moving-chip-animation/grid-mobile.png'})`,
                    backgroundSize: 'cover',
                    width: '150%',
                    height: '100%',
                    left: '-25%',
                    right: '-25%',
                    position: 'absolute',
                    zIndex: -1,
                }}
            />
            <Box sx={{ position: 'absolute', left: -70, top: 80 }}>
                <EmissionFactorChip type={ChipType.CustomerShiptheory} />
            </Box>
            <Box sx={{ position: 'absolute', right: 60, top: 500 }}>
                <EmissionFactorChip type={ChipType.CustomerBeacon} />
            </Box>
            <Box sx={{ position: 'absolute', left: 0, top: 925 }}>
                <EmissionFactorChip type={ChipType.CustomerGoComet} />
            </Box>
            <Stack spacing={1} sx={{ width: '100%' }}>
                {children}
            </Stack>
        </Box>
    )
}
